import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api, resolveImg } from "@/lib/api";
import { useContent } from "@/context/ContentContext";
import SEO from "@/components/SEO";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { Reveal } from "@/components/Reveal";
import { ArrowLeft } from "@phosphor-icons/react";

export default function ProjectDetail() {
  const { slug } = useParams();
  const content = useContent();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/projects/${slug}`).then(({ data }) => setProject(data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="bg-background min-h-screen flex flex-col items-center justify-center gap-6">
        <p className="font-serif text-4xl">Project not found</p>
        <Link to="/projects" className="text-xs uppercase tracking-[0.2em] hover-underline">Back to projects</Link>
      </div>
    );
  }
  if (!project) return <div className="min-h-screen bg-background" />;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "CreativeWork",
    name: project.title,
    description: project.excerpt || project.description,
    image: resolveImg(project.cover_image),
    locationCreated: project.location,
  };

  return (
    <div className="bg-background min-h-screen">
      <SEO
        title={project.seo_title || `${project.title} — Nat Habitat`}
        description={project.seo_description || project.excerpt}
        image={resolveImg(project.cover_image)}
        path={`/projects/${project.slug}`}
        type="article"
        jsonLd={jsonLd}
      />
      <Nav brand={content?.brand?.name} />

      <section className="pt-32 md:pt-44">
        <div className="mx-auto max-w-[1600px] px-6 md:px-12">
          <button onClick={() => navigate("/projects")} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-foreground/60 hover:text-foreground transition-colors mb-12" data-testid="back-link">
            <ArrowLeft size={16} /> All Projects
          </button>
          <div className="grid md:grid-cols-12 gap-8 items-end">
            <div className="md:col-span-8">
              <Reveal>
                <p className="text-xs uppercase tracking-[0.25em] text-accent">{project.category_name}</p>
              </Reveal>
              <Reveal delay={0.05}>
                <h1 className="mt-4 font-serif font-light text-5xl sm:text-6xl md:text-7xl tracking-tight leading-none">
                  {project.title}
                </h1>
              </Reveal>
            </div>
            <div className="md:col-span-4 grid grid-cols-3 gap-4 text-sm">
              <Meta label="Location" value={project.location} />
              <Meta label="Area" value={project.area} />
              <Meta label="Year" value={project.year} />
            </div>
          </div>
        </div>
      </section>

      <section className="mt-14 md:mt-20">
        <Reveal>
          <div className="w-full overflow-hidden">
            <img src={resolveImg(project.cover_image)} alt={project.title} className="w-full h-[55vh] md:h-[85vh] object-cover" />
          </div>
        </Reveal>
      </section>

      <section className="py-20 md:py-32">
        <div className="mx-auto max-w-[1600px] px-6 md:px-12 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.25em] text-accent">Overview</p>
            </Reveal>
          </div>
          <div className="md:col-span-7 md:col-start-5">
            <Reveal>
              <p className="font-serif font-light text-2xl md:text-3xl leading-[1.4] text-foreground/90">
                {project.description || project.excerpt}
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {project.images?.length > 0 && (
        <section className="pb-24 md:pb-40">
          <div className="mx-auto max-w-[1600px] px-6 md:px-12 grid md:grid-cols-2 gap-6 md:gap-8">
            {project.images.map((img, i) => (
              <Reveal key={i} delay={(i % 2) * 0.08} className={i % 3 === 0 ? "md:col-span-2" : ""}>
                <div className="overflow-hidden">
                  <img
                    src={resolveImg(img)}
                    alt={`${project.title} detail ${i + 1}`}
                    className={`w-full object-cover ${i % 3 === 0 ? "h-[60vh] md:h-[90vh]" : "h-[400px] md:h-[620px]"}`}
                  />
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      <Footer content={content} />
    </div>
  );
}

function Meta({ label, value }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{label}</p>
      <p className="mt-1 text-foreground">{value || "—"}</p>
    </div>
  );
}
